<template>
  <div>
    <form class="reset-password-form" novalidate @submit.prevent="submitResetPassword">
      <div class="reset-password-form__section">
        <FormInput
          v-model="password"
          name="password"
          ref="password"
          :type="passwordType"
          :error="passwordError"
          :label="$t('reset-password.password-input')"
          v-validate="'required'"
          has-gray-background
          autocomplete="on"
          v-on:blur="handleBlur"
        >
          <template #top-right>
            <button
              class="reset-password-form__toggle-password"
              type="button"
              @click.prevent="togglePassword"
              tabindex="-1"
            >
              <BaseIcon name="eye" v-if="passwordHidden" />
              <BaseIcon name="eye-off" v-else />
              {{ passwordHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
            </button>
          </template>
        </FormInput>
        <FormInput
          v-model="confirmation"
          name="confirmation"
          ref="confirmation"
          :type="confirmationType"
          :error="confirmationError"
          :label="$t('reset-password.confirmation-input')"
          v-validate="'required|confirmed:password'"
          has-gray-background
          autocomplete="on"
          v-on:blur="handleBlur"
        >
          <template #top-right>
            <button
              class="reset-password-form__toggle-password"
              type="button"
              @click.prevent="toggleConfirmation"
              tabindex="-1"
            >
              <BaseIcon name="eye" v-if="confirmationHidden" />
              <BaseIcon name="eye-off" v-else />
              {{ confirmationHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
            </button>
          </template>
        </FormInput>
        <PasswordCriteria :password="password" />
      </div>

      <FormButton
        hasIconRight
        type="submit"
        class="reset-password-form__reset-btn"
        :is-outlined="!isFormValid"
        :disabled="!isFormValid"
      >
        {{ $t('reset-password.button') }}
        <BaseIcon name="arrow-right" />
      </FormButton>
    </form>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import FormButton from '@/components/form/FormButton.vue';
import FormInput from '@/components/form/FormInput.vue';
import PasswordCriteria from '@/components/PasswordCriteria';
import { validatePassword } from '@/util/password-util';

import { mapActions } from 'vuex';

export default {
  components: {
    BaseIcon,
    FormButton,
    FormInput,
    PasswordCriteria,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  inject: ['$validator'],
  data() {
    return {
      password: '',
      confirmation: '',
      passwordHidden: true,
      confirmationHidden: true,
    };
  },
  computed: {
    isFormValid() {
      return this.passwordCriteria.valid && this.password === this.confirmation;
    },
    passwordCriteria() {
      return validatePassword(this.password);
    },
    passwordError() {
      const formError = this.errors.items.find((x) => x.field === 'password');

      return formError ? formError.msg : undefined;
    },
    confirmationError() {
      const formError = this.errors.items.find((x) => x.field === 'confirmation');

      return formError ? formError.msg : undefined;
    },
    passwordType() {
      return this.passwordHidden ? 'password' : 'text';
    },
    confirmationType() {
      return this.confirmationHidden ? 'password' : 'text';
    },
  },
  mounted() {
    const refInput = this.$refs.password.$el.querySelector('input');
    if (refInput) {
      refInput.focus();
    }
  },
  methods: {
    ...mapActions('auth', ['changePassword']),
    submitResetPassword() {
      this.$validator.validate();
      if (this.isFormValid) {
        this.changePassword({ password: this.password, token: this.token });
        this.$emit('submited');
      }
    },
    togglePassword() {
      this.passwordHidden = !this.passwordHidden;
    },
    toggleConfirmation() {
      this.confirmationHidden = !this.confirmationHidden;
    },
    resetForm() {
      this.password = '';
      this.confirmation = '';
      this.passwordHidden = true;
      this.confirmationHidden = true;
      this.$validator.reset();
      this.errors.clear();
    },
    handleBlur() {
      window.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="scss">
.reset-password-form {
  &__section {
    margin-bottom: 20px;
    width: 100%;
  }

  &__title {
    color: $color-normal;
    font-size: 2rem;
    font-weight: bold;
    margin: 1rem 0;

    @media screen and (max-width: 784px) {
      font-size: 1.2rem;
    }
  }

  &__subtitle {
    color: $color-normal-pale;
    font-size: 1.125rem;
    font-family: Raleway;
    font-size: 1.125rem;
    font-weight: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    font-stretch: normal;
    margin-bottom: 3rem;

    @media screen and (max-width: 784px) {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
  }

  &__toggle-password {
    align-items: center;
    background: none;
    border: none;
    color: $color-normal-pale;
    cursor: pointer;
    display: flex;
    font-size: $font-size-xsmall;
    outline: none;

    &:hover,
    &:active,
    &:focus {
      color: $color-accent;
    }

    .feather {
      height: 14px;
      margin-right: 4px;
      width: 14px;
    }
  }

  &__reset-btn {
    margin: 2.5rem auto 0 auto;
  }
}
</style>
